import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { formatAmount, isCalorieUnit } from '@goaltree/backend/convex/lib/habitMeasure'
import { useMutation, useQuery } from 'convex/react'
import { Check, Utensils } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { NumberLogSheet, presetMeasure, useQuickLog } from '@/components/number-habit'
import type { ChatMessage } from '@/hooks/use-coach-chat'
import { parseTaskFields } from '@/lib/tags'
import { handleUsageLimit } from '@/lib/usage-limit'

type Estimate = NonNullable<ChatMessage['mealEstimate']>

// Coach reply to a meal photo: the itemised estimate, logged into the user's
// calorie habit (created from the Calories preset when they have none). Once
// added it shows the day's new total against the target.
export function MealEstimateCard({
  messageId,
  estimate,
}: {
  messageId?: string
  estimate: Estimate
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const habits = useQuery(api.nodes.getHabits)
  const createQuickTask = useMutation(api.nodes.createQuickTask)
  const markLogged = useMutation(api.mealPhoto.markLogged).withOptimisticUpdate(
    (localStore, { messageId: id, habitId }) => {
      const h = habitId
        ? localStore.getQuery(api.nodes.getHabits, {})?.find((x) => x._id === habitId)
        : undefined
      const day =
        h?.measure && h.measureToday ? { m: h.measure, total: h.measureToday.total } : null
      for (const { args, value } of localStore.getAllQueries(api.coachHistory.getHistory)) {
        if (!value?.some((m) => m._id === id && m.mealEstimate && !m.mealEstimate.loggedAt))
          continue
        localStore.setQuery(
          api.coachHistory.getHistory,
          args,
          value.map((m) =>
            m._id === id && m.mealEstimate
              ? {
                  ...m,
                  mealEstimate: {
                    ...m.mealEstimate,
                    loggedAt: Date.now(),
                    ...(day && {
                      loggedTotal: day.total,
                      loggedTarget: day.m.target,
                      loggedUnit: day.m.unit,
                    }),
                  },
                }
              : m,
          ),
        )
      }
    },
  )
  const quickLog = useQuickLog()
  const [busy, setBusy] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const habit = habits?.find((h) => h.measure && !h.frozen && isCalorieUnit(h.measure.unit))
  const progress = habit?.measure
    ? {
        measure: habit.measure,
        total: habit.measureToday?.total ?? 0,
        count: habit.measureToday?.count ?? 0,
        title: parseTaskFields(habit.title).title,
      }
    : null
  const note = estimate.items
    .map((i) => i.name)
    .join(', ')
    .slice(0, 200)
  const logged = (habitId: Id<'nodes'>) => {
    if (messageId)
      void markLogged({ messageId: messageId as Id<'coachMessages'>, habitId, locale: lang })
  }

  async function add() {
    if (busy) return
    setBusy(true)
    try {
      let habitId = habit?._id
      let p = progress
      if (!habitId || !p) {
        const preset = presetMeasure(t, 'calories')
        habitId = await createQuickTask({
          title: preset.title,
          isHabit: true,
          measure: preset.measure,
          icon: preset.icon,
          recurrenceTzOffset: new Date().getTimezoneOffset(),
        })
        p = { measure: preset.measure, total: 0, count: 0, title: preset.title }
      }
      const r = await quickLog(habitId, p, estimate.total, { note, source: 'photo' })
      if (r) logged(habitId)
    } catch (e) {
      if (!handleUsageLimit(e)) toast.error(t('numberHabit.logError'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="w-full rounded-xl border border-border/60 bg-card/40 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Utensils className="size-3.5 shrink-0 text-muted-foreground" />
        <p className="flex-1 text-xs font-semibold text-foreground">
          {t('numberHabit.meal.title')}
        </p>
        <p className="font-mono text-sm font-bold tabular-nums text-foreground">
          ~{formatAmount(estimate.total, lang)} kcal
        </p>
      </div>
      <ul className="flex flex-col gap-1">
        {estimate.items.map((item, i) => (
          <li key={i} className="flex items-baseline gap-2 text-xs">
            <span className="min-w-0 flex-1 truncate text-foreground">
              {item.name}
              {item.qty && <span className="text-muted-foreground"> · {item.qty}</span>}
            </span>
            <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
              {formatAmount(item.kcal, lang)}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">{t('numberHabit.meal.rough')}</p>
      {estimate.loggedAt ? (
        <p className="mt-2.5 flex items-center gap-1.5 rounded-lg bg-primary/15 px-3 py-2 text-xs font-bold tabular-nums text-primary-text">
          <Check className="size-3.5" strokeWidth={2.6} />
          {estimate.loggedTotal != null && estimate.loggedTarget != null
            ? t('numberHabit.meal.addedTotal', {
                total: formatAmount(estimate.loggedTotal, lang),
                target: formatAmount(estimate.loggedTarget, lang),
                unit: estimate.loggedUnit ?? 'kcal',
              })
            : t('numberHabit.meal.logged', { title: progress?.title ?? '' })}
        </p>
      ) : (
        <div className="mt-2.5 flex gap-2">
          <button
            type="button"
            disabled={busy || habits === undefined}
            onClick={() => void add()}
            className="h-9 flex-1 cursor-pointer rounded-lg bg-primary px-3 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {progress
              ? t('numberHabit.meal.add', { amount: formatAmount(estimate.total, lang) })
              : t('numberHabit.meal.trackAndAdd', { amount: formatAmount(estimate.total, lang) })}
          </button>
          {habit && progress && (
            <button
              type="button"
              onClick={() => setAdjustOpen(true)}
              className="h-9 cursor-pointer rounded-lg border border-border/60 px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
            >
              {t('numberHabit.meal.adjust')}
            </button>
          )}
        </div>
      )}
      {habit && progress && (
        <NumberLogSheet
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          habitId={habit._id}
          progress={progress}
          initialAmount={estimate.total}
          initialNote={note}
          source="photo"
          onLogged={() => logged(habit._id)}
        />
      )}
    </div>
  )
}
